<template>
    <HeaderCardsLayout variant="No-Logo">
        <template #headline>
            <ScenarioSwitcher
                v-if="scenario"
                :has-unsaved-changes="scenarioHasUnsavedChanges"
                :scenario-index="scenario.index"
                :scenario-index-max-digits="4"
                :scenario-year="scenario.year"
                :scenario-name="scenario.nameShort"
            />
            <NoScenarioFallback v-if="showScenarioFallback" />
            <LoadingScenarioFailed v-if="loadingScenarioFailed" />
            <LoadingScenario v-if="scenarioIsLoading" />
        </template>

        <template v-if="scenario" #buttons>
            <Button
                size="Base"
                variant="Neutral-Strong"
                :disabled-explanation="scenarioButtonsDisabledExplanation"
                :status="saveScenarioSettingButtonStatus"
                :status-message="saveScenarioSettingButtonStatusMessage"
                @click="send({ type: 'save.scenario' })"
            >
                <template #icon>
                    <AllDoneIcon class="size-5" />
                </template>
                {{ $t('save') }}
            </Button>
            <Button
                size="Base"
                variant="Danger-Strong"
                :disabled-explanation="scenarioButtonsDisabledExplanation"
                :status="discardScenarioSettingButtonStatus"
                @click="send({ type: 'discard.scenario.changes' })"
            >
                <template #icon>
                    <DeleteForeverIcon class="size-5" />
                </template>
                {{ $t('discard-changes') }}
            </Button>
        </template>

        <template #body>
            <PaddedContentNoBottom>
                <div class="@container">
                    <div class="bg-gray-50 h-full -mx-4 p-4 w-max min-w-full">
                        <!--
                          `sticky` makes this a stacking context, so the dropdowns inside cannot rise above
                          the timeline on their own: `z-30` lifts the whole toolbar over the timeline's panes
                          instead.
                        -->
                        <div class="flex flex-wrap items-end justify-between gap-4 sticky left-0 z-30 w-[100cqw]">
                            <div class="flex items-end gap-4">
                                <TimelineIntervalChanger
                                    :current-quarter="snapshotQuarterNumber"
                                    :current-year="snapshotYearNumber"
                                    :minimal-year="MINIMAL_YEAR"
                                    :maximal-year="MAXIMAL_YEAR"
                                    :warning-no-allocations="snapshot.context.warningNoAllocations"
                                    :is-time-span-modified="isDisplayedTimeSpanModified"
                                    @change-quarter="
                                        (quarter: QuarterNumbers) => send({ type: 'change.quarter', quarter })
                                    "
                                    @change-year="(year: number) => send({ type: 'change.year', year })"
                                    @click-previous-quarter="send({ type: 'click.previousQuarter' })"
                                    @click-next-quarter="send({ type: 'click.nextQuarter' })"
                                />

                                <OptionSwitch
                                    name="timeline-locations-display"
                                    :active-index="snapshotActiveIndex"
                                    :label="$t('timeline-locations-display-label')"
                                    @change-option="
                                        (index) =>
                                            send({
                                                type: 'change.timelineDisplay',
                                                timelineDisplay: getIdFromIndex(index) ?? 'sections',
                                            })
                                    "
                                >
                                    <OptionSwitchEntry
                                        v-for="entry of timelineOptionSwitchEntries"
                                        :key="entry.id"
                                        :icon="entry.icon"
                                        :index="entry.index"
                                        :label="entry.label"
                                    />
                                </OptionSwitch>
                            </div>
                            <div class="flex items-end gap-4">
                                <AllocationDesignerSelectionFilter
                                    ref="event-variant-filter"
                                    type="Event"
                                    :sections="eventFilterSections"
                                    :label="getSelectionFilterLabel('Event')"
                                    :status="eventFilterStatus"
                                    :allocation-count="eventFilterVisibleCount"
                                    :plus-count="eventVariantPlusCount"
                                    :minus-count="eventVariantMinusCount"
                                    :undo-changes="onEventFilterUndoChanges"
                                    :toggle-section-is-expanded="
                                        (sectionId) => eventFilterSend({ type: 'toggle.section.expand', sectionId })
                                    "
                                    :toggle-visibility="onEventFilterToggleVisibility"
                                    :make-main-variant-visible="onEventFilterMakeMainVariantVisible"
                                    :make-active-variants-visible="onEventFilterMakeActiveVariantsVisible"
                                    :toggle-non-active-variants="onEventFilterToggleNonActiveVariants"
                                />
                                <AllocationDesignerSelectionFilter
                                    ref="project-variant-filter"
                                    type="Project"
                                    :sections="projectFilterSections"
                                    :label="getSelectionFilterLabel('Project')"
                                    :status="projectFilterStatus"
                                    :allocation-count="projectFilterVisibleCount"
                                    :plus-count="projectVariantPlusCount"
                                    :minus-count="projectVariantMinusCount"
                                    :undo-changes="onProjectFilterUndoChanges"
                                    :toggle-section-is-expanded="
                                        (sectionId) => projectFilterSend({ type: 'toggle.section.expand', sectionId })
                                    "
                                    :toggle-visibility="onProjectFilterToggleVisibility"
                                    :make-main-variant-visible="onProjectFilterMakeMainVariantVisible"
                                    :make-active-variants-visible="onProjectFilterMakeActiveVariantsVisible"
                                    :toggle-non-active-variants="onProjectFilterToggleNonActiveVariants"
                                />
                                <AllocationDesignerSelectionFilter
                                    ref="external-event-variant-filter"
                                    type="ExternalEvent"
                                    :sections="externalEventFilterSections"
                                    :label="getSelectionFilterLabel('ExternalEvent')"
                                    :status="externalEventFilterStatus"
                                    :allocation-count="externalEventFilterVisibleCount"
                                    :plus-count="externalEventVariantPlusCount"
                                    :minus-count="externalEventVariantMinusCount"
                                    :undo-changes="onExternalEventFilterUndoChanges"
                                    :toggle-section-is-expanded="
                                        (sectionId) =>
                                            externalEventFilterSend({ type: 'toggle.section.expand', sectionId })
                                    "
                                    :toggle-visibility="onExternalEventFilterToggleVisibility"
                                    :toggle-override-visibility="onExternalEventFilterToggleOverrideVisibility"
                                    :make-main-variant-visible="onExternalEventFilterMakeMainVariantVisible"
                                    :make-active-variants-visible="onExternalEventFilterMakeActiveVariantsVisible"
                                    :toggle-non-active-variants="onExternalEventFilterToggleNonActiveVariants"
                                />
                            </div>
                        </div>

                        <Message
                            v-if="snapshot.matches({ loadData: { loading: 'timelineData' } })"
                            status="info"
                            class="mt-4"
                        >
                            {{ $t('loading-timeline-data') }}
                        </Message>

                        <Message
                            v-if="loadingTimelineError"
                            class="mt-4"
                            status="error"
                            :headline="$t('error-fetching-timeline-data')"
                        >
                            {{ loadingTimelineError }}
                        </Message>

                        <Message
                            v-if="loadingScenarioFailed"
                            class="mt-4"
                            status="error"
                            :headline="$t('scenario-not-found')"
                        >
                            <div class="flex flex-col gap-2">
                                <div>{{ $t('scenario-not-found-error-text') }}</div>

                                <LinkButton
                                    size="Base"
                                    variant="Danger-Strong"
                                    :to="{ name: 'allocation-designer' }"
                                    class="mt-4"
                                >
                                    {{ $t('plan-without-scenario') }}
                                </LinkButton>
                            </div>
                        </Message>

                        <Timeline
                            v-if="
                                timelineData &&
                                !snapshot.matches({ loadData: { loading: 'timelineData' } }) &&
                                site &&
                                start &&
                                end &&
                                allocations &&
                                countsMap
                            "
                            class="mt-4"
                            scroll-container="Page"
                            :header-name="site.nameShort"
                            :header-link-target="routeForLocation('Site', site.id)"
                            :start-date-time="start"
                            :end-date-time="end"
                            :previous-month-button="previousMonthButtonStatus"
                            :next-month-button="nextMonthButtonStatus"
                            :conflicts-by-day="conflictsByDay"
                            :site-conflicts-count="siteConflictsCount"
                            :update-allocation="updateAllocation"
                            @expand-to-previous-month="send({ type: 'click.previousMonth' })"
                            @expand-to-next-month="send({ type: 'click.nextMonth' })"
                        >
                            <template #first-column-rows>
                                <ExternalEventFirstColumnCell
                                    v-for="row in externalEventFirstColumnCells"
                                    v-bind="row"
                                    :key="row.rowId"
                                />
                                <!-- Sections View -->
                                <template v-if="showSections">
                                    <template v-for="section in sections" :key="section.id">
                                        <SectionFirstColumnCell
                                            v-bind="section"
                                            :section-id="section.id"
                                            :conflicts-count="section.conflictsCount"
                                            :is-expanded="section.isExpanded"
                                            :icon="section.icon"
                                            @toggle-section-expanded="toggleSectionExpanded(section.id)"
                                        >
                                            <template #label="{ labelId }">
                                                <SectionAllocationCountLabel
                                                    :id="labelId"
                                                    :label="section.label"
                                                    :allocation-count="section.allocationCount"
                                                />
                                            </template>
                                        </SectionFirstColumnCell>
                                        <!-- v-show to keep location cells mounted (selection registration) when collapsed. -->
                                        <AllocationFirstColumnCell
                                            v-for="location in section.locations"
                                            v-show="section.isExpanded"
                                            v-bind="location"
                                            :key="location.id"
                                            :is-visible="section.isExpanded"
                                            :location-id="location.id"
                                            :row-id="`location-${location.id}`"
                                            :expandable="location.expandable"
                                            :link-target="location.linkTarget"
                                            :level="location.level"
                                            :icon="location.icon"
                                        >
                                            <template #label="{ labelId, countTextColorClass }">
                                                <LocationAllocationCountLabel
                                                    :id="labelId"
                                                    :label="location.label"
                                                    :allocation-count="location.allocationCount"
                                                    :count-text-color-class="countTextColorClass"
                                                />
                                            </template>

                                            <template #link-tooltip-header>
                                                {{ location.label }}
                                            </template>

                                            <template #link-tooltip-text>
                                                {{ $t('open-detail-page') }}
                                            </template>
                                        </AllocationFirstColumnCell>
                                    </template>
                                </template>
                                <!-- Hierarchy View -->
                                <template v-else>
                                    <AllocationFirstColumnCell
                                        v-for="location in hierarchyRows"
                                        v-show="location.isVisible"
                                        :key="location.id"
                                        :is-visible="location.isVisible"
                                        :location-id="location.id"
                                        :type-category="location.typeCategory"
                                        :parent-id="location.parentId"
                                        :row-id="`location-${location.id}`"
                                        :expandable="location.expandable"
                                        :link-target="location.linkTarget"
                                        :level="location.level"
                                        :icon="location.icon"
                                        @toggle-location-expanded="toggleLocationExpanded(location.id)"
                                    >
                                        <template #label="{ labelId, countTextColorClass }">
                                            <LocationAllocationCountLabel
                                                :id="labelId"
                                                :label="location.label"
                                                :allocation-count="location.allocationCount"
                                                :count-text-color-class="countTextColorClass"
                                            />
                                        </template>

                                        <template #link-tooltip-header>
                                            {{ location.label }}
                                        </template>

                                        <template #link-tooltip-text>
                                            {{ $t('open-detail-page') }}
                                        </template>
                                    </AllocationFirstColumnCell>
                                </template>
                            </template>

                            <template #days-rows>
                                <LayoutRow v-for="row in externalEventLayoutRows" v-bind="row" :key="row.rowId">
                                    <Allocation
                                        v-for="allocation in row.allocations"
                                        v-bind="allocation"
                                        :key="allocation.id"
                                        :allocation-usages="usages"
                                    >
                                        <template #tooltip-header>
                                            <div class="w-full text-center">{{ allocation.tooltipHeader }}</div>
                                        </template>
                                        <template #tooltip-text>
                                            <InternationalTooltip
                                                v-if="allocation.tooltipType === 'International'"
                                                :headline="allocation.tooltipHeadline"
                                            />
                                            <FederalStatesTooltip
                                                v-if="allocation.tooltipType === 'National'"
                                                :headline="allocation.tooltipHeadline"
                                                :federal-states="allocation.federalStates"
                                            />
                                        </template>
                                    </Allocation>
                                </LayoutRow>

                                <!-- Sections View -->
                                <template v-if="showSections">
                                    <template v-for="section in sectionLayoutRows" :key="section.id">
                                        <SectionDaysRow
                                            v-bind="section"
                                            @toggle-section-expanded="toggleSectionExpanded(section.id)"
                                        />
                                        <template v-if="section.isExpanded">
                                            <LayoutRow
                                                v-for="row in section.locationRows"
                                                v-bind="row"
                                                :key="row.rowId"
                                            >
                                                <Allocation
                                                    v-for="allocation in row.allocations"
                                                    v-bind="allocation"
                                                    :key="allocation.id"
                                                    :allocation-usages="usages"
                                                >
                                                    <template #tooltip-header>
                                                        {{ $t('click-to-edit-allocation') }}
                                                    </template>
                                                    <template #tooltip-text>
                                                        <AllocationTooltip
                                                            v-if="allocation.allocationTooltip"
                                                            v-bind="allocation.allocationTooltip"
                                                        />
                                                    </template>
                                                </Allocation>
                                            </LayoutRow>
                                        </template>
                                    </template>
                                </template>

                                <!-- Hierarchy View -->
                                <template v-else>
                                    <LayoutRow v-for="row in hierarchyLayoutRows" v-bind="row" :key="row.rowId">
                                        <Allocation
                                            v-for="allocation in row.allocations"
                                            v-bind="allocation"
                                            :key="allocation.id"
                                            :allocation-usages="usages"
                                        />
                                    </LayoutRow>
                                </template>
                            </template>
                        </Timeline>
                    </div>
                </div>
            </PaddedContentNoBottom>
        </template>
    </HeaderCardsLayout>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import { ButtonStatus } from '@/components/Button/Button.types';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContentNoBottom from '@/components/Layout/PaddedContentNoBottom.vue';
import Message from '@/components/Message/Message.vue';
import { useOptionSwitch } from '@/components/OptionSwitch/composables/useOptionSwitch';
import OptionSwitch from '@/components/OptionSwitch/OptionSwitch.vue';
import LoadingScenario from '@/components/ScenarioSwitcher/LoadingScenario.vue';
import LoadingScenarioFailed from '@/components/ScenarioSwitcher/LoadingScenarioFailed.vue';
import NoScenarioFallback from '@/components/ScenarioSwitcher/NoScenarioFallback.vue';
import ScenarioSwitcher from '@/components/ScenarioSwitcher/ScenarioSwitcher.vue';
import Allocation from '@/components/Timeline/allocation/Allocation.vue';
import AllocationFirstColumnCell from '@/components/Timeline/components/AllocationFirstColumnCell.vue';
import AllocationTooltip from '@/components/Timeline/components/AllocationTooltip.vue';
import ExternalEventFirstColumnCell from '@/components/Timeline/components/ExternalEventFirstColumnCell.vue';
import LayoutRow from '@/components/Timeline/components/LayoutRow.vue';
import LocationAllocationCountLabel from '@/components/Timeline/components/LocationAllocationCountLabel.vue';
import SectionAllocationCountLabel from '@/components/Timeline/components/SectionAllocationCountLabel.vue';
import SectionDaysRow from '@/components/Timeline/components/SectionDaysRow.vue';
import SectionFirstColumnCell from '@/components/Timeline/components/SectionFirstColumnCell.vue';
import { useAllocationsByLocation } from '@/components/Timeline/composables/useAllocationsByLocation';
import { useConflictMarkers } from '@/components/Timeline/composables/useConflictMarkers';
import { useHierarchyRows } from '@/components/Timeline/composables/useHierarchyRows';
import { useSectionRows } from '@/components/Timeline/composables/useSectionRows';
import { useTimelineConflicts } from '@/components/Timeline/composables/useTimelineConflicts';
import { useTimelineLocations } from '@/components/Timeline/composables/useTimelineLocations';
import { useTimelineSections } from '@/components/Timeline/composables/useTimelineSections';
import { useTimelineValidation } from '@/components/Timeline/composables/useTimelineValidation';
import FederalStatesTooltip from '@/components/Timeline/FederalStatesTooltip.vue';
import InternationalTooltip from '@/components/Timeline/InternationalTooltip.vue';
import { countConflicts } from '@/components/Timeline/logic/conflictCounts';
import { timelineOptionSwitchEntries } from '@/components/Timeline/Timeline.optionSwitch';
import Timeline from '@/components/Timeline/Timeline.vue';
import {
    MAXIMAL_YEAR,
    MINIMAL_YEAR,
} from '@/components/TimelineIntervalChanger/composables/useTimelineIntervalChanger';
import TimelineIntervalChanger from '@/components/TimelineIntervalChanger/TimelineIntervalChanger.vue';
import { useExternalEventAggregationRows } from '@/external_event/composables/useExternalEventAggregationRows';
import { ExternalEventEventStatus } from '@/generated/graphql.ts';
import { routeForLocation } from '@/location/location';
import { $t } from '@/plugins/fluent';
import { useHeadSafe } from '@unhead/vue';
import { useApolloClient } from '@vue/apollo-composable';
import { useRouteQuery } from '@vueuse/router';
import { useMachine } from '@xstate/vue';
import { DateTime, QuarterNumbers } from 'luxon';
import { computed, Ref, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import OptionSwitchEntry from '../components/OptionSwitch/OptionSwitchEntry.vue';
import {
    designerStateMachine,
    eventFilterMachine,
    externalEventFilterMachine,
    projectFilterMachine,
} from './allocationDesigner';
import AllocationDesignerSelectionFilter from './AllocationDesignerSelectionFilter.vue';
import { calculateDisplayedStart, calculateEndFromStart } from './composables/designerMachineFunctions';
import { getSelectionFilterLabel, useDesignerFilterData } from './composables/useDesignerFilterData';
import { useDesignerTimelineData } from './composables/useDesignerTimelineData';
import { useDesignerTimelineState } from './composables/useDesignerTimelineState';

const scenarioId = useRouteQuery<string>('scenario-id', '');
const quarter = useRouteQuery<string>('quarter', '');
const year = useRouteQuery<string>('year', '');
const timelineDisplay = useRouteQuery<string>('timeline-display', '');

const client = useApolloClient().client;

const DEFAULT_ACTIVE_EVENT_STATES = ['active-idea', 'active-option', 'active-fix', 'passive', 'completed', 'canceled'];
const DEFAULT_ACTIVE_PROJECT_STATES = ['active-idea', 'active-option', 'active-fix', 'completed'];
const DEFAULT_ACTIVE_EXTERNAL_EVENT_STATES = [
    ExternalEventEventStatus.Fix,
    ExternalEventEventStatus.Option,
    ExternalEventEventStatus.Completed,
];

// create filter state machines

const {
    snapshot: eventFilterSnapshot,
    send: eventFilterSend,
    actorRef: actorRefEventFilter,
} = useMachine(eventFilterMachine, {
    input: { type: 'Event', activeGroupStates: DEFAULT_ACTIVE_EVENT_STATES },
});

const {
    snapshot: projectFilterSnapshot,
    send: projectFilterSend,
    actorRef: actorRefProjectFilter,
} = useMachine(projectFilterMachine, {
    input: { type: 'Project', activeGroupStates: DEFAULT_ACTIVE_PROJECT_STATES },
});

const {
    snapshot: externalEventFilterSnapshot,
    send: externalEventFilterSend,
    actorRef: actorRefExternalEventFilter,
} = useMachine(externalEventFilterMachine, {
    input: { type: 'ExternalEvent', activeGroupStates: DEFAULT_ACTIVE_EXTERNAL_EVENT_STATES },
});

// create designer state machine

const { snapshot, send } = useMachine(designerStateMachine, {
    input: {
        client,
        scenarioId: scenarioId.value,
        eventFilterRef: actorRefEventFilter,
        projectFilterRef: actorRefProjectFilter,
        externalEventFilterRef: actorRefExternalEventFilter,
    },
});

const scenario = computed(() => snapshot.value.context.scenario);
const setting = computed(() => snapshot.value.context.setting);
const snapshotQuarterNumber = computed(() => Number(snapshot.value.context.quarter) as QuarterNumbers);
const snapshotYear = computed(() => snapshot.value.context.year);
const snapshotYearNumber = computed(() => Number(snapshotYear.value));
const snapshotTimelineDisplay = computed(() => snapshot.value.context.timelineDisplay);
const snapshotActiveIndex = computed(() => getIndexFromId(snapshotTimelineDisplay.value));
const start = computed(() => snapshot.value.context.start);
const end = computed(() => snapshot.value.context.end);
const monthsBefore = computed(() => snapshot.value.context.monthsBefore);
const monthsAfter = computed(() => snapshot.value.context.monthsAfter);
const loadingTimelineError = computed(() => snapshot.value.context.loadingTimelineError);

const {
    status: eventFilterStatus,
    sections: eventFilterSections,
    visibleIds: eventFilterVisibleIds,
    visibleCount: eventFilterVisibleCount,
    plusCount: eventVariantPlusCount,
    minusCount: eventVariantMinusCount,
    changedStates: eventFilterChangedStates,
    undoChanges: onEventFilterUndoChanges,
    setMainVariantVisible: onEventFilterMakeMainVariantVisible,
    setActiveVariantsVisible: onEventFilterMakeActiveVariantsVisible,
    toggleNonActiveVariants: onEventFilterToggleNonActiveVariants,
    toggleVisibility: onEventFilterToggleVisibility,
} = useDesignerFilterData(eventFilterSnapshot, eventFilterSend, send, start, end);

const {
    status: projectFilterStatus,
    sections: projectFilterSections,
    visibleIds: projectFilterVisibleIds,
    visibleCount: projectFilterVisibleCount,
    plusCount: projectVariantPlusCount,
    minusCount: projectVariantMinusCount,
    changedStates: projectFilterChangedStates,
    undoChanges: onProjectFilterUndoChanges,
    setMainVariantVisible: onProjectFilterMakeMainVariantVisible,
    setActiveVariantsVisible: onProjectFilterMakeActiveVariantsVisible,
    toggleNonActiveVariants: onProjectFilterToggleNonActiveVariants,
    toggleVisibility: onProjectFilterToggleVisibility,
} = useDesignerFilterData(projectFilterSnapshot, projectFilterSend, send, start, end);

const {
    status: externalEventFilterStatus,
    sections: externalEventFilterSections,
    visibleIds: externalEventFilterVisibleIds,
    overrideVisibilityFilterIds: externalEventOverrideVisibilityFilterIds,
    visibleCount: externalEventFilterVisibleCount,
    plusCount: externalEventVariantPlusCount,
    minusCount: externalEventVariantMinusCount,
    changedStates: externalEventFilterChangedStates,
    undoChanges: onExternalEventFilterUndoChanges,
    setMainVariantVisible: onExternalEventFilterMakeMainVariantVisible,
    setActiveVariantsVisible: onExternalEventFilterMakeActiveVariantsVisible,
    toggleNonActiveVariants: onExternalEventFilterToggleNonActiveVariants,
    toggleVisibility: onExternalEventFilterToggleVisibility,
    toggleOverrideVisibility: onExternalEventFilterToggleOverrideVisibility,
} = useDesignerFilterData(externalEventFilterSnapshot, externalEventFilterSend, send, start, end);

const showScenarioFallback = computed(
    () =>
        !scenario.value &&
        !snapshot.value.matches({ withScenario: 'loading' }) &&
        !snapshot.value.matches({ withScenario: 'scenarioLoadingFailed' }),
);

const loadingScenarioFailed = computed(() => snapshot.value.matches({ withScenario: 'scenarioLoadingFailed' }));
const scenarioIsLoading = computed(() => snapshot.value.matches({ withScenario: 'loading' }));

// just a mock until the DesignerPage gets editing functions
async function updateAllocation() {
    return { phaseUpdates: [], isOptional: false, referenceAllocationId: '' };
}

const { isDisplayedTimeSpanModified, previousMonthButtonStatus, nextMonthButtonStatus } = useDesignerTimelineState(
    start,
    monthsBefore,
    monthsAfter,
    snapshotTimelineDisplay,
);

watch(
    [scenarioId, quarter, year, timelineDisplay],
    ([newScenarioId, newQuarter, newYear, newTimelineDisplay]) => {
        // machine initialization
        if (snapshot.value.matches('initial')) {
            if (newScenarioId) {
                send({ type: 'with.scenario' });
            } else if (newQuarter || newYear || newTimelineDisplay) {
                send({
                    type: 'with.routeparams',
                    quarter: newQuarter,
                    year: newYear,
                    timelineDisplay: newTimelineDisplay,
                });
            } else {
                send({ type: 'with.nothing' });
            }
        } else {
            if (newQuarter !== snapshot.value.context.quarter) {
                send({ type: 'change.quarter', quarter: Number(newQuarter) as QuarterNumbers });
            }
            if (newYear !== snapshot.value.context.year) {
                send({ type: 'change.year', year: Number(newYear) });
            }
            if (newTimelineDisplay !== snapshot.value.context.timelineDisplay) {
                send({ type: 'change.timelineDisplay', timelineDisplay: newTimelineDisplay });
            }
        }
    },
    { immediate: true },
);

watch(
    () => snapshot.value.context.quarter,
    (value) => {
        quarter.value = value;
    },
);

watch(
    () => snapshot.value.context.year,
    (value) => {
        year.value = value;
    },
);

watch(
    () => snapshot.value.context.timelineDisplay,
    (value) => {
        timelineDisplay.value = value;
    },
);

const { getIdFromIndex, getIndexFromId } = useOptionSwitch(timelineDisplay, timelineOptionSwitchEntries);

const scenarioHasUnsavedChanges = ref(false);

const timelineData = computed(() => snapshot.value.context.timelineData);

const { site, rooms, areas, usages, allocations, countsMap, externalEventExecutionPhasePosition } =
    useDesignerTimelineData(timelineData, eventFilterVisibleIds, projectFilterVisibleIds);

const { conflictsByDay } = useTimelineConflicts(allocations, start, end);

const { layoutRows: externalEventLayoutRows, firstColumnCells: externalEventFirstColumnCells } =
    useExternalEventAggregationRows(
        start,
        end,
        externalEventExecutionPhasePosition.value,
        externalEventFilterVisibleIds,
        externalEventOverrideVisibilityFilterIds,
    );

const showSections = computed(() => snapshotTimelineDisplay.value === 'sections');

const { getLocations } = useTimelineLocations(site, rooms, areas, showSections);
const locations = computed(() => getLocations().value);

const { sections: baseSections, toggleSectionExpanded } = useTimelineSections();

const conflictMarkersByLocation = useConflictMarkers(allocations, start, end);
const allocationsByLocation = useAllocationsByLocation(allocations);

// Site-level conflict total for the header cell: the sum of every location's conflict spans.
// Resolved conflicts don't exist yet, so everything counts as unresolved for now.
const siteConflictsCount = computed(() => {
    let total = 0;
    for (const markers of conflictMarkersByLocation.value.values()) {
        total += countConflicts(markers);
    }
    return { resolved: 0, unresolved: total, total };
});

const { hierarchyRows, toggleLocationExpanded } = useHierarchyRows({
    locations,
    allocationsByLocation,
    conflictMarkersByLocation,
    startDateTime: start,
    endDateTime: end,
    enabled: computed(() => !showSections.value),
});

const { sections } = useSectionRows({
    baseSections,
    locations,
    allocationsByLocation,
    conflictMarkersByLocation,
    startDateTime: start,
    endDateTime: end,
});

useTimelineValidation(locations, allocations);

// Read-only timeline: create buttons are always hidden.
const sectionLayoutRows = computed(() =>
    sections.value.map((section) => ({
        ...section,
        locationRows: section.locations.map((location) => ({
            rowId: `location-${location.id}`,
            isFirstRow: false,
            layoutAlgorithm: 'Compact' as const,
            createButtonStatus: { type: 'Hidden' as const },
            conflictMarkers: location.conflictMarkers,
            allocations: location.allocations,
        })),
    })),
);

const hierarchyLayoutRows = computed(() =>
    hierarchyRows.value
        .filter((location) => location.isVisible)
        .map((location) => ({
            rowId: `location-${location.id}`,
            isFirstRow: false,
            layoutAlgorithm: 'Compact' as const,
            createButtonStatus: { type: 'Hidden' as const },
            conflictMarkers: location.conflictMarkers,
            allocations: location.allocations,
        })),
);

const timelineDisplayChanged = computed(() => {
    if (setting.value) {
        return showSections.value !== setting.value.showSections;
    }
    return timelineDisplay.value !== 'sections';
});

const timeRangeChanged = computed(() => {
    if (setting.value) {
        return (
            snapshotYearNumber.value !== setting.value.year ||
            snapshotQuarterNumber.value !== setting.value.quarter ||
            (start.value && !start.value.equals(DateTime.fromISO(setting.value.start))) ||
            (end.value && !end.value.equals(DateTime.fromISO(setting.value.end))) ||
            monthsBefore.value !== setting.value.monthsBefore ||
            monthsAfter.value !== setting.value.monthsAfter
        );
    } else {
        if (scenario.value) {
            const scenarioYear = scenario.value.year;
            const scenarioStart = DateTime.fromISO(`${scenarioYear}-01-01`);
            const defaultStart = calculateDisplayedStart(scenarioStart, 0);
            const defaultEnd = calculateEndFromStart(defaultStart, 0);

            return (
                snapshotYearNumber.value !== defaultStart.year ||
                snapshotQuarterNumber.value !== defaultStart.quarter ||
                (start.value && !start.value.equals(defaultStart)) ||
                (end.value && defaultEnd && !end.value.equals(defaultEnd)) ||
                monthsBefore.value > 0 ||
                monthsAfter.value > 0
            );
        } else {
            const defaultStart = calculateDisplayedStart(DateTime.now(), 0);
            const defaultEnd = calculateEndFromStart(defaultStart, 0);
            return (
                snapshotYearNumber.value !== defaultStart.year ||
                snapshotQuarterNumber.value !== defaultStart.quarter ||
                (start.value && defaultStart && !start.value.equals(defaultStart)) ||
                (end.value && defaultEnd && !end.value.equals(defaultEnd)) ||
                monthsBefore.value > 0 ||
                monthsAfter.value > 0
            );
        }
    }
});

const setVisibleIdsChanged = computed(() => {
    return (
        eventFilterChangedStates.value.length > 0 ||
        projectFilterChangedStates.value.length > 0 ||
        externalEventFilterChangedStates.value.length > 0
    );
});

const changesFromDefault = computed(() => {
    return timelineDisplayChanged.value || timeRangeChanged.value || setVisibleIdsChanged.value;
});

const scenarioButtonsDisabledExplanation = computed(() => {
    if (!auth.isAdmin()) {
        return $t('no-permission-to-edit');
    }
    if (!scenario.value || (scenario.value && snapshot.value.matches({ withScenario: 'scenarioLoadingFailed' }))) {
        return $t('requires-existing-scenario');
    }
    if (
        (snapshot && snapshot.value.matches('createSetting')) ||
        snapshot.value.matches('updateSetting') ||
        snapshot.value.matches('updateVisibilities')
    ) {
        return $t('saving');
    }
    if (!changesFromDefault.value) {
        return $t('no-unsaved-changes');
    }
});

const saveScenarioSettingButtonStatus: Ref<ButtonStatus | undefined> = computed(() => {
    if (
        (snapshot && snapshot.value.matches({ withScenario: 'loading' })) ||
        snapshot.value.matches('createSetting') ||
        snapshot.value.matches('updateSetting') ||
        snapshot.value.matches('updateVisibilities')
    ) {
        return 'Loading';
    }
    if (snapshot && snapshot.value.context.scenarioSaved) {
        return 'Success';
    }
    if (snapshot && snapshot.value.matches('errorOnSaveScenarioSetting')) {
        return 'Error';
    }
    return 'Default';
});

const saveScenarioSettingButtonStatusMessage = computed(() => {
    if (
        (snapshot && snapshot.value.matches({ withScenario: 'loading' })) ||
        snapshot.value.matches('createSetting') ||
        snapshot.value.matches('updateSetting') ||
        snapshot.value.matches('updateVisibilities')
    ) {
        return $t('saving');
    }
    if (snapshot && snapshot.value.context.scenarioSaved) {
        return $t('saving-success');
    }
    if (snapshot && snapshot.value.matches('errorOnSaveScenarioSetting')) {
        return $t('saving-error');
    }
});

const discardScenarioSettingButtonStatus: Ref<ButtonStatus | undefined> = computed(() => {
    if (
        (snapshot && snapshot.value.matches({ withScenario: 'loading' })) ||
        snapshot.value.matches('createSetting') ||
        snapshot.value.matches('updateSetting') ||
        snapshot.value.matches('updateVisibilities')
    ) {
        return 'Default';
    }
    if (snapshot && snapshot.value.context.scenarioSaved) {
        return 'Success';
    }
    if (snapshot && snapshot.value.matches('errorOnSaveScenarioSetting')) {
        return 'Error';
    }
    return 'Default';
});

onBeforeRouteLeave(() => {
    if (changesFromDefault.value) {
        if (!confirm($t('leave-page-warning'))) return false;
    }
});

const tabTitle = computed(() => snapshot.value.context.tabTitle);
useHeadSafe({ title: () => `${tabTitle.value} - ${$t('allocation-designer')}` });
</script>
